import { ApiError, NetworkError, api } from './apiClient.js';
import { detectEnvironment, isIos, isStandalone, type Environment } from './platform.js';
import { base64UrlToBytes, type PushKeyDTO, type PushRole } from '@shared/push.js';

/**
 * התראות Push בצד הדפדפן. ההרשאה נבקשת רק בלחיצה של המשתמש (דרישה של הדפדפנים, ובקשה מפורשת של בעל המשחק),
 * והמנוי נרשם בשרת לפי תפקיד: 'player' (משימות והודעות) או 'admin' (הגשות פתרון ופתקים מהשטח).
 */

export type PushSupport = 'supported' | 'needs-install' | 'unsupported';

export function pushSupport(): PushSupport {
  const hasApis = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (hasApis) return 'supported';
  // ב-iPhone/iPad ההתראות זמינות רק לאפליקציה שנוספה למסך הבית — בלשונית Safari ה-API פשוט לא קיים
  if (isIos() && !isStandalone()) return 'needs-install';
  return 'unsupported';
}

/** למה אין כרגע אפשרות להפעיל התראות בכלל (ולא רק "עוד לא אישרו") — כדי להסביר למשתמש מה לעשות */
export type PushUnavailableReason = 'in-app-browser' | 'old-ios' | 'unsupported-browser';

/**
 * null = אפשר להפעיל התראות, או שההסבר נמצא במקום אחר (iPhone/iPad בלשונית Safari — הודעת ההתקנה). בדפדפן מובנה של
 * אפליקציה אחרת (וואטסאפ, אינסטגרם...) אין התראות גם אם המכשיר עצמו תומך בהן, ולכן בודקים אותו קודם.
 */
export function pushUnavailableReason(env: Environment, support: PushSupport): PushUnavailableReason | null {
  if (support === 'supported') return null;
  if (env.inAppBrowser) return 'in-app-browser';
  if (support === 'needs-install') return null;
  if (env.os === 'ios' && env.iosVersion !== null && env.iosVersion < 16.4) return 'old-ios';
  return 'unsupported-browser';
}

/** הסבר ידידותי לכשל בהרשמה להתראות — השגיאות הגולמיות של הדפדפן ("Registration failed - push service error") לא עוזרות לאף אחד */
export function describePushError(e: unknown, env?: Environment): string {
  if (e instanceof NetworkError) return 'אין חיבור לשרת — בדקו את הקליטה ונסו שוב';
  if (e instanceof ApiError) return e.message;
  if (e instanceof DOMException) {
    switch (e.name) {
      case 'NotAllowedError':
        return 'ההרשאה להתראות לא ניתנה — אפשר לאפשר אותה בהגדרות האתר בדפדפן';
      case 'AbortError': {
        const oem = (env ?? detectEnvironment()).oem;
        return oem === 'huawei'
          ? 'במכשירי Huawei/Honor בלי שירותי Google אין התראות אתר — האפליקציה תתעדכן כשהיא פתוחה'
          : 'שירות ההתראות של הדפדפן לא הגיב. בדקו שיש חיבור לאינטרנט (ובאנדרואיד — ששירותי Google Play פעילים) ונסו שוב';
      }
      case 'InvalidStateError':
        return 'האפליקציה עדיין נטענת — נסו שוב בעוד רגע';
      case 'NotSupportedError':
      case 'SecurityError':
        return 'הדפדפן הזה לא מאפשר התראות (בגלישה פרטית או בלי חיבור מאובטח הן לא נתמכות)';
    }
  }
  return e instanceof Error && e.message ? e.message : 'הרישום להתראות נכשל';
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

const ENDPOINTS: Record<PushRole, string> = { player: '/api/player/push', admin: '/api/admin/push' };
const FLAG_PREFIX = 'pmr-push-';

/** האם המשתמש הפעיל התראות לתפקיד הזה במכשיר הזה (נשמר מקומית — לדפדפן יש מנוי אחד שמשותף לשני התפקידים) */
export function pushRoleEnabledHere(role: PushRole): boolean {
  try {
    return localStorage.getItem(FLAG_PREFIX + role) === '1';
  } catch {
    return false;
  }
}

function setRoleFlag(role: PushRole, on: boolean): void {
  try {
    if (on) localStorage.setItem(FLAG_PREFIX + role, '1');
    else localStorage.removeItem(FLAG_PREFIX + role);
  } catch {
    // בלי localStorage (מצב פרטי) — ההתראות עדיין עובדות, רק המצב לא נזכר בין טעינות
  }
}

const SW_READY_TIMEOUT_MS = 8_000;

/** ה-Service Worker אינו פעיל בסביבת פיתוח — בלי מגבלת זמן ההמתנה אליו הייתה נתקעת לנצח */
function readyRegistration(): Promise<ServiceWorkerRegistration> {
  const timeout = new Promise<never>((_, reject) =>
    window.setTimeout(() => reject(new Error('ה-Service Worker עדיין לא פעיל — נסו שוב בעוד רגע')), SW_READY_TIMEOUT_MS),
  );
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
  const current = sub.options?.applicationServerKey;
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((v, i) => v === key[i]);
}

/** מנוי קיים של הדפדפן, או חדש. מנוי שנוצר עם מפתח שרת אחר (למשל אחרי איפוס השרת) מוחלף */
async function ensureSubscription(reg: ServiceWorkerRegistration, publicKey: string): Promise<PushSubscription> {
  const key = base64UrlToBytes(publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, key)) {
    await sub.unsubscribe();
    sub = null;
  }
  return sub ?? reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key as BufferSource });
}

async function registerOnServer(role: PushRole): Promise<void> {
  const [reg, { publicKey }] = await Promise.all([readyRegistration(), api.get<PushKeyDTO>('/api/player/push/key')]);
  const sub = await ensureSubscription(reg, publicKey);
  await api.post(`${ENDPOINTS[role]}/subscribe`, sub.toJSON());
}

export type EnableResult = { ok: true } | { ok: false; error: string; denied?: boolean };

/** מבקש הרשאת התראות (חייב להיקרא ישירות מלחיצה של המשתמש) ורושם את המכשיר לתפקיד */
export async function enablePush(role: PushRole): Promise<EnableResult> {
  if (pushSupport() !== 'supported') return { ok: false, error: 'הדפדפן הזה לא תומך בהתראות' };
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch (e) {
    return { ok: false, error: describePushError(e) };
  }
  if (permission !== 'granted') {
    return permission === 'denied'
      ? { ok: false, denied: true, error: 'ההתראות חסומות בדפדפן — אפשר לאפשר אותן בהגדרות האתר' }
      : { ok: false, error: 'ההתראות לא אושרו' };
  }
  try {
    await registerOnServer(role);
    setRoleFlag(role, true);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: describePushError(e) };
  }
}

/** מבטל את ההתראות לתפקיד במכשיר הזה (המנוי של הדפדפן עצמו מבוטל רק כשלא נשאר בו אף תפקיד) */
export async function disablePush(role: PushRole): Promise<void> {
  setRoleFlag(role, false);
  const reg = await readyRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const res = await api.post<{ ok: boolean; remaining: boolean }>(`${ENDPOINTS[role]}/unsubscribe`, {
    endpoint: sub.endpoint,
  });
  if (!res.remaining) await sub.unsubscribe();
}

/**
 * מכשיר שכבר הפעיל התראות: מוודא שהמנוי קיים גם בשרת (נמחק אם בסיס הנתונים אופס, או אם הדפדפן החליף מנוי).
 * לא מבקש הרשאה ולא מציג כלום; כשל (Offline וכד') נבלע וינסה שוב בכניסה הבאה.
 */
export async function syncPushSubscription(role: PushRole): Promise<void> {
  if (pushSupport() !== 'supported' || Notification.permission !== 'granted' || !pushRoleEnabledHere(role)) return;
  try {
    await registerOnServer(role);
  } catch {
    // ננסה שוב בכניסה הבאה
  }
}

/** סוגר התראות שכבר הוצגו — מי שנכנס לאפליקציה כבר ראה אותן, ואין סיבה שיישארו בשורת ההתראות */
export async function clearDeliveredNotifications(): Promise<void> {
  if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const list = (await reg?.getNotifications()) ?? [];
    for (const n of list) n.close();
  } catch {
    // לא קריטי
  }
}
